import { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

export type AdminBreadcrumbItem = {
  label: string;
  href?: string;
  icon?: ReactNode;
};

type AdminBreadcrumbProps = {
  items: AdminBreadcrumbItem[];
  /**
   * 最後一項（當前頁面）要用什麼標籤渲染。
   * 傳入 "h1" 可以讓頁面標題直接併入麵包屑同一列，取代另外獨立一行的大標題，藉此降低頁首高度；
   * 預設 "span"，用於單純的導覽層級（不代表頁面標題）。
   */
  currentTag?: "span" | "h1";
};

/**
 * 後台共用麵包屑導覽列。
 * 由 AdminLayout 統一渲染，各後台頁面只需透過 `sectionLabel` / `title` props 帶入內容，
 * 不需要在個別頁面重複撰寫麵包屑的 HTML／樣式。
 */
export function AdminBreadcrumb({ items, currentTag = "span" }: AdminBreadcrumbProps) {
  const CurrentTag = currentTag;

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 flex-wrap items-center gap-1 sm:gap-1.5">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li className="flex min-w-0 items-center gap-1 sm:gap-1.5" key={`${item.label}-${index}`}>
              {index > 0 && (
                <ChevronRight aria-hidden="true" className="shrink-0 text-[#c7d0cb]" size={isLast ? 16 : 14} />
              )}
              {item.href && !isLast ? (
                <Link
                  className="flex min-w-0 items-center gap-1 truncate text-xs font-bold text-[#7b877f] transition hover:text-[#315746] sm:text-sm"
                  to={item.href}
                >
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </Link>
              ) : isLast ? (
                <CurrentTag
                  aria-current="page"
                  className="m-0 flex min-w-0 items-center gap-1.5 truncate text-sm font-extrabold leading-tight text-[#203d32] sm:text-base"
                >
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </CurrentTag>
              ) : (
                <span className="flex min-w-0 items-center gap-1 truncate text-xs font-bold text-[#7b877f] sm:text-sm">
                  {item.icon}
                  <span className="truncate">{item.label}</span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
